"use client";

import { useCallback, useEffect, useState } from "react";
import { loadedDeploymentId } from "@/lib/observability/stale-deployment";

/** Intervalle entre deux vérifications (en plus du retour sur l'onglet). */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Prévient qu'une nouvelle version du site est en ligne et propose de
 * rafraîchir.
 *
 * Un onglet resté ouvert exécute l'ancien bundle contre le nouveau serveur, ce
 * qui finit par casser (« Server Action introuvable », « e[o] is not a
 * function »). Plutôt que d'attendre l'erreur puis de recharger d'autorité, on
 * prévient : la personne choisit son moment et ne perd pas ce qu'elle est en
 * train de saisir.
 */
export function NewVersionBanner() {
  const [outdated, setOutdated] = useState(false);

  const check = useCallback(async () => {
    const loaded = loadedDeploymentId();
    if (!loaded) return; // hors Vercel (dev local) : rien à comparer
    try {
      const res = await fetch("/api/version", { cache: "no-store" });
      if (!res.ok) return;
      const { deploymentId } = (await res.json()) as { deploymentId: string | null };
      if (deploymentId && deploymentId !== loaded) setOutdated(true);
    } catch {
      // Hors ligne ou requête annulée : on retentera plus tard.
    }
  }, []);

  useEffect(() => {
    void check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [check]);

  if (!outdated) return null;

  return (
    <div
      role="status"
      data-testid="new-version-banner"
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-line bg-card px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)] sm:px-6"
    >
      <div className="mx-auto flex max-w-[560px] flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:text-left">
        <p className="text-[13px] leading-[1.45] text-body">
          Une nouvelle version du site est disponible.
        </p>
        <button
          type="button"
          data-testid="new-version-refresh"
          onClick={() => window.location.reload()}
          className="w-full shrink-0 rounded-pill bg-gold px-5 py-2.5 text-[12px] font-semibold uppercase tracking-wider text-ink transition-colors hover:bg-gold-deep sm:w-auto"
        >
          Rafraîchir la page
        </button>
      </div>
    </div>
  );
}
